/* eslint-disable */
import qs from 'qs';
import config from 'config';
import { axios, clearDefaults, updateApiBaseUrl } from './config';
import { resource, cacheKey, authMethods, cloudEnvs } from 'appConstants';
import { extLocalStore } from 'util/localStorage';
import { replaceSpecialChars } from 'util/helpers';
import { decodeJwt } from '../../util';
import { updateWsUrl } from 'redux/middlewares';

let candidates__forceCacheUpdate = false;
let refreshTokenIntervalId = null;

const euApiBaseUrl = 'https://api-aws-eu.prd.textrecruit.tools';
const euLookupUrl = 'https://lookup-aws-eu.prd.textrecruit.tools';

export function lookup(username) {
	const urls = [`${config.lookupApiUrl}/users`, `${euLookupUrl}/users`];
	const promises = urls.map(async (url) => {
		const resp = await fetch(url, {
			method: 'POST',
			responseType: 'json',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({ username })
		});
		return resp.json();
	});
	return Promise.allSettled(promises)
		.then((resp) => {
			let returnResp = resp[0].value;
			resp.forEach((r) => {
				if (r.status === 'fulfilled' && r.value && r.value.providers) {
					const { providers = [] } = r.value;
					providers.forEach((p) => {
						if (p.cloudEnvironment === cloudEnvs.EU) {
							updateApiBaseUrl({ cloudEnvironment: p.cloudEnvironment });
						}
						if (p.authType.toLowerCase() !== authMethods.LEGACY) {
							returnResp = r.value;
						}
					});
				}
			});
			if (!returnResp) {
				return Promise.reject(new Error('Lookup failed: no valid response from any endpoint'));
			}
			return returnResp;
		})
		.catch((e) => Promise.reject(e));
}

export function sso(username) {
	return axios.get('/remote_auth_info', { params: { username } });
}

export async function login(username, password) {
	const data = new URLSearchParams({
		username,
		password,
		remember_me: true,
		product: 'TextRecruit',
		client_ver: '2.0.0',
		app_name: 'ext'
	});
	const headers = {
		accept: 'application/json, text/plain, */*',
		'content-type': 'application/x-www-form-urlencoded'
	};
	const params = null;
	try {
		const defaultResp = axios.post('user/authenticate', data, { params, headers });
		extLocalStore.realTimeTicket = defaultResp.ticket;
		return Promise.resolve(defaultResp);
	} catch (defaultError) {
		if (defaultError.message === 'Your password has expired') {
			return Promise.reject(defaultError);
		}
		try {
			const euResp = await axios.post(
				`${euApiBaseUrl}/api/v1/textrecruit/user/authenticate`,
				data,
				{ params, headers }
			);
			updateApiBaseUrl({ cloudEnvironment: cloudEnvs.EU });
			extLocalStore.realTimeTicket = euResp.ticket;
			return Promise.resolve(euResp);
		} catch (euError) {
			if (euError.message === 'Customer ID is required') {
				return Promise.reject(defaultError);
			}
			return Promise.reject(euError);
		}
	}
}

export function logout() {
	if (refreshTokenIntervalId) {
		clearInterval(refreshTokenIntervalId);
	}
	return axios.get('user/logout');
}

export async function refreshToken() {
	/**
	 * isRefreshing flag is added to prevent concurrent token-refresh requests
	 * in other opened browser-tabs.
	 */
	extLocalStore.setItem('isRefreshing', true);
	const postBody = {
		token_request: true
	};
	return axios
		.post(resource.TOKEN_INFO, postBody, {
			forceUpdate: true
		})
		.then((response) => {
			extLocalStore.setItems({
				isRefreshing: false,
				lastRefreshedTime: new Date().getTime(),
				[cacheKey.TOKEN]: response.token
			});
			extLocalStore.realTimeTicket = response.ticket;
			return response;
		})
		.catch((error) => {
			extLocalStore.setItem('isRefreshing', false);
			clearDefaults();
			return Promise.reject(error);
		});
}

function refreshTokenInterval(token) {
	if (token && !extLocalStore.getItem('isRefreshing')) {
		refreshToken();
	}
}

export async function sessionInfo() {
	return axios
		.get('/user/login', { forceUpdate: true })
		.then((response) => {
			updateWsUrl(response.userInfo.realtimeUrl);
			extLocalStore.realTimeTicket = response.ticket;
			// Initiate refresh-token for legacy auth users.
			if (!extLocalStore.auth0 && !refreshTokenIntervalId) {
				extLocalStore.setItem('lastRefreshedTime', new Date().getTime());
				const token = extLocalStore.getItem(cacheKey.TOKEN);
				const jwtPayload = decodeJwt(token);

				// set interval delay to 24hrs if token duration is longer than 24 days.
				let intervalMs = jwtPayload.duration * 0.9 * 1e3;

				if (intervalMs > Math.pow(2, 31) - 1) {
					intervalMs = 24 * 3600 * 1e3;
				}
				refreshTokenIntervalId = setInterval(() => refreshTokenInterval(token), intervalMs);
			}
			return response;
		})
		.catch((error) => {
			clearDefaults();
			return Promise.reject(error);
		});
}

export function addCandidate(firstName, lastName, phoneNumber, source, sourceId, channel) {
	const reqBody = {
		first_name: firstName,
		last_name: lastName,
		phone: phoneNumber,
		channel
	};

	if (source && sourceId) {
		reqBody.source = source;
		reqBody.candidate_id = sourceId;
	}

	return axios.post(`candidate/add?${qs.stringify(reqBody)}`).then((response) => {
		candidates__forceCacheUpdate = true;
		return response;
	});
}

export function fetchCandidateByPhone(phone) {
	return axios.get('candidate_by_phone', { params: { phone } });
}

export function fetchOrAdd(firstName, lastName, phoneNumber, channel) {
	return fetchCandidateByPhone(phoneNumber)
		.then((response) => response)
		.catch(() => addCandidate(firstName, lastName, phoneNumber, null, null, channel));
}

export function searchCandidates(value) {
	const pureValue = replaceSpecialChars(value);
	const isNumber = !isNaN(pureValue);
	const searchValue = isNumber ? pureValue : value;
	const fieldName = isNumber ? 'phoneNumber' : 'fullName';
	const postBody = {
		account_id: axios.defaults.params.account_id,
		items_per_page: 15,
		sort: '-created',
		nocount: true,
		filter: [
			{ fieldName, type: 'Like', value: searchValue },
			{ fieldName: 'restricted', type: 'NotEquals', value: true }
		]
	};
	return axios.post(resource.CANDIDATES, postBody);
}

export function getCandidates(pageNumber = 1) {
	const postBody = {
		account_id: axios.defaults.params.account_id,
		items_per_page: 10,
		page: pageNumber,
		sort: '-id',
		nocount: true
	};
	return axios
		.post(resource.CANDIDATES, postBody, {
			forceUpdate: candidates__forceCacheUpdate
		})
		.then((response) => {
			candidates__forceCacheUpdate = false;
			return response;
		});
}

export function fetchCandidateById(candidateId) {
	return axios.post(
		resource.GET_CANDIDATE_BY_ID,
		{
			candidate_id: candidateId
		},
		{
			forceUpdate: true
		}
	);
}

/**
 * @param {String} dataSource       => Name of the source. E.g.: Bullhorn, taleo
 * @param {String} externalId       => Object-ID provided by the source
 * @param {String} externalIdType   => Taleo specific
 */
export function fetchCandidateBySource(dataSource, externalId, externalIdType) {
	const params = { source: dataSource, candidate_id: externalId };
	return axios.get(resource.GET_CANDIDATE_BY_SOURCE, { params, forceUpdate: true });
}

export function addCandidateToCampaign(jobId, candidateId) {
	return axios.post(
		resource.ADD_CANDIDATE_TO_CAMPAIGN + qs.stringify({ job_id: jobId, candidate_ids: candidateId })
	);
}

export function addCampaign(args) {
	return axios.post(resource.ADD_CAMPAIGN + qs.stringify({ ...args }));
}

export function getCampaign(campaignId) {
	return axios.get(resource.CAMPAIGN_API, {
		params: { job_id: campaignId },
		/**
		 * TODO: forceUpdate ensures this API resp. is not cached.
		 *
		 * It's because response.candidates data will not be updated
		 * if the same campaign has been added to a other candidate(s)
		 * within the last 5 mins.
		 *
		 * Ideally we can cache it and update response.candidates
		 * when the same campaign has been added to other candidate(s).
		 * The solution is a little complicated and for now we can
		 * punt on caching. -Amir
		 */
		forceUpdate: true
	});
}

export function removeFromCampaign(campaignId, candidateId) {
	return axios.post(
		resource.REMOVE_CANDIDATE_FROM_CAMPAIGN +
			qs.stringify({ job_id: campaignId, candidate_id: candidateId })
	);
}

export function searchCampaigns(value, itemsPerPage) {
	const formattedFilter = JSON.stringify([
		{ fieldName: 'name', type: 'Like', value },
		{ fieldName: 'status', type: 'Equals', value: 'open' }
	]);

	const params = {
		items_per_page: itemsPerPage || 10,
		sort: '-created',
		filter: formattedFilter
	};
	return axios.get(resource.CAMPAIGNS_API, { params });
}

export function getConversation(candidateId, markAsRead, threadId, actingUser) {
	const requestBody = {
		id: candidateId,
		page: 1,
		items_per_page: 100,
		nocount: true,
		do_not_mark_read: !markAsRead,
		show_link_clicks: true
	};
	if (threadId) {
		requestBody.thread_id = threadId;
	}
	if (actingUser) {
		requestBody.acting_user = actingUser;
	}
	return axios.post(resource.GET_MESSAGES_API, requestBody, { forceUpdate: true });
}

export function getThreadsWithUnreadMessages(accountType) {
	return axios.get(resource.MESSAGE_THREADS, {
		params: {
			username: extLocalStore.username,
			page: 1,
			account_type: accountType.toUpperCase(),
			items_per_page: 100,
			sort: '-time',
			filter: JSON.stringify([{ fieldName: 'read', type: 'Equals', value: false }]),
			nocount: true
		},
		forceUpdate: true
	});
}

export function markMessageRead(messageId) {
	return axios.get(resource.MESSAGE, {
		params: { message_id: messageId, status: 'markRead' },
		forceUpdate: true
	});
}

export function getMessageTemplates() {
	return axios.get(resource.MESSAGE_TEMPLATES, { params: { items_per_page: 100, type: 'user' } });
}

export async function sendSingleMessage(
	candidateId,
	text,
	actingUser,
	twilioTemplateId,
	contentVariables
) {
	const requestBody = { candidate_id: candidateId, text };
	if (actingUser) {
		requestBody.acting_user = actingUser;
	}
	if (twilioTemplateId) {
		requestBody.twilio_template_id = twilioTemplateId;
	}
	if (contentVariables) {
		requestBody.content_variables = JSON.parse(contentVariables);
	}
	return axios.post(resource.SEND_MESSAGE, requestBody);
}

export function getICIMSCandidateData(candidateId) {
	return axios.get(resource.INTEGRATION_DATA, {
		params: { candidate_id: candidateId, source: 'icims' }
	});
}

export function validatePhoneNumber(phone) {
	return axios.get(resource.VALIDATE_PHONE_NUMBER, { params: { phone } });
}

export function getMessageThreads(type, pageNumber = 1) {
	return axios.get(resource.MESSAGE_THREADS, {
		params: {
			username: extLocalStore.username,
			account_type: type.toUpperCase(),
			items_per_page: 100,
			page: pageNumber,
			sort: '-time',
			nocount: true
		},
		forceUpdate: true
	});
}

export function searchThreads(value, type) {
	const pureValue = replaceSpecialChars(value);
	const isNumber = !isNaN(pureValue);
	const searchValue = isNumber ? pureValue : value;
	const fieldName = isNumber ? 'phoneNumber' : 'candidateName';
	return axios
		.get(resource.MESSAGE_THREADS, {
			params: {
				items_per_page: 15,
				sort: '-time',
				account_type: type.toUpperCase(),
				page: 1,
				nocount: true,
				filter: JSON.stringify([{ fieldName, type: 'Like', value: searchValue }])
			}
		})
		.then((response) => {
			response.inboxType = type;
			return response;
		});
}

export function updateCandidateChannel(candidateId, channel) {
	const params = { candidate_id: candidateId, channel };
	return axios.post(`candidate/update?${qs.stringify(params)}`);
}

export function updateUserPreferences(blacklistedDomains, preferredPhoneNumberRegions) {
	const params = {};
	if (preferredPhoneNumberRegions) {
		params.preferred_phone_regions = JSON.stringify(preferredPhoneNumberRegions);
	}
	if (blacklistedDomains) {
		params.blacklisted_domains = JSON.stringify(blacklistedDomains);
	}
	return axios.post(`user/update/preferences?${qs.stringify(params)}`);
}

export function outboundAriConfig(threadId) {
	return axios.get('sequence/outbound_ari?', { params: { thread_id: threadId } });
}

export function enableAri(threadId, candidateId, sequenceId, text, initialMessageOverride) {
	const params = {
		thread_id: threadId,
		candidate_id: candidateId,
		sequence_id: sequenceId,
		mark_read: true
	};

	if (text) {
		params.text = text;
	}
	if (initialMessageOverride) {
		params.initial_message_override = true;
	}
	return axios.post(`sequence/enable_ari?${qs.stringify(params)}`);
}

export function disableAri(threadId) {
	return axios.post(`sequence/disable_ari?${qs.stringify({ thread_id: threadId })}`);
}
